import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Send, AlertTriangle, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const SKIP_REASONS = {
  no_openings: 'Nothing was open',
  no_weekend: 'Free nights, but no full Friday + Saturday',
  no_rates: 'A weekend was free, but has no rate set',
  no_recipients: 'Nobody in the group has opted in',
  already_ran_today: 'Already ran today'
};

// What actually happened to one recipient's email, in plain words.
function describeRecipient(r) {
  if (r.bounced) return { label: 'Bounced', tone: 'bad', detail: r.bounceReason };
  if (r.status === 'failed') return { label: 'Failed', tone: 'bad', detail: r.lastError };
  if (r.status === 'canceled') return { label: 'Blocked', tone: 'warn', detail: r.lastError };
  if (r.deliveredAt) return { label: 'Delivered', tone: 'good', detail: null };
  if (r.status === 'sent' || r.sentAt) return { label: 'Sent', tone: 'good', detail: null };
  if (r.status === 'queued') return { label: 'Waiting to send', tone: 'wait', detail: null };
  return { label: r.status || 'Unknown', tone: 'warn', detail: r.lastError };
}

const TONE_COLOR = {
  good: '#2b5d2b',
  bad: '#8a2f2f',
  warn: '#7a5b1b',
  wait: '#5a5a5a'
};

const BLANK_FORM = {
  groupId: '',
  templateId: '',
  dayOfWeek: 3,
  timezone: 'America/New_York',
  lookaheadDays: 10,
  discountPercent: 20,
  isActive: true
};

/** A plain 'YYYY-MM-DD' formatted without the timezone shifting it a day back. */
function formatDay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'
  });
}

const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}`);

function VacancyEmailSchedule() {
  const { getAuthHeader } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [logMeta, setLogMeta] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [schRes, grpRes, tplRes, logRes] = await Promise.all([
        fetch(`${API_URL}/api/vacancy-emails/schedules`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/user-groups?mailable=1`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/email-templates`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/vacancy-emails/log`, { headers: getAuthHeader() })
      ]);
      const [sch, grp, tpl, lg] = await Promise.all([
        schRes.json(), grpRes.json(), tplRes.json(), logRes.json()
      ]);
      setSchedules(sch.data || []);
      setGroups(grp.groups || []);
      setTemplates((tpl.data || []).filter((t) => t.is_active));
      setLog(lg.data || []);
      setLogMeta(lg.meta || null);
    } catch {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadPreview = useCallback(async (lookaheadDays, discountPercent) => {
    setPreview(null);
    try {
      const res = await fetch(
        `${API_URL}/api/vacancy-emails/preview?lookaheadDays=${lookaheadDays}&discountPercent=${discountPercent}`,
        { headers: getAuthHeader() }
      );
      const data = await res.json();
      if (res.ok) setPreview(data.data);
    } catch {
      // Preview is advisory; a failure here must not block editing.
    }
  }, [getAuthHeader]);

  // Keep the preview in step with the window and discount being edited.
  useEffect(() => {
    if (!showForm) return;
    const t = setTimeout(() => loadPreview(form.lookaheadDays || 10, form.discountPercent || 0), 300);
    return () => clearTimeout(t);
  }, [showForm, form.lookaheadDays, form.discountPercent, loadPreview]);

  const openCreate = () => {
    setEditingId(null);
    setForm(BLANK_FORM);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      groupId: String(s.group_id),
      templateId: s.template_id,
      dayOfWeek: s.day_of_week,
      timezone: s.timezone,
      lookaheadDays: s.lookahead_days,
      discountPercent: Number(s.discount_percent),
      isActive: s.is_active
    });
    setError(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK_FORM);
    setPreview(null);
    setError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/vacancy-emails/schedules/${editingId}`
        : `${API_URL}/api/vacancy-emails/schedules`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          groupId: parseInt(form.groupId, 10),
          templateId: form.templateId,
          dayOfWeek: parseInt(form.dayOfWeek, 10),
          timezone: form.timezone,
          lookaheadDays: parseInt(form.lookaheadDays, 10),
          discountPercent: parseFloat(form.discountPercent),
          isActive: form.isActive
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed.'); return; }
      cancelForm();
      fetchAll();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule? Queued emails are not affected.')) return;
    try {
      const res = await fetch(`${API_URL}/api/vacancy-emails/schedules/${id}`, {
        method: 'DELETE', headers: getAuthHeader()
      });
      if (res.ok) fetchAll(); else setError('Delete failed.');
    } catch {
      setError('Delete failed.');
    }
  };

  const handleRunNow = async (s) => {
    const who = s.opted_in_count === 1 ? '1 person' : `${s.opted_in_count} people`;
    if (!window.confirm(
      `Queue this email now for ${who} in ${s.group_name}?\n\n` +
      'This is a real send, not a preview. The queue runner picks it up on its next pass.'
    )) return;

    setRunning(s.id);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${API_URL}/api/vacancy-emails/schedules/${s.id}/run`, {
        method: 'POST', headers: getAuthHeader()
      });
      const data = await res.json();
      if (!res.ok) {
        setError([data.error, data.message].filter(Boolean).join(' — ') || 'Run failed.');
        return;
      }

      const r = data.data;
      setNotice(
        r.recipientsQueued > 0
          ? `Queued ${r.recipientsQueued} email${r.recipientsQueued === 1 ? '' : 's'} covering ${r.weekendsFound} open weekend${r.weekendsFound === 1 ? '' : 's'}.`
          : `Nothing queued — ${SKIP_REASONS[r.skippedReason] || r.skippedReason || 'nothing to send'}.`
      );
      fetchAll();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setRunning(null);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

  const noTemplates = templates.length === 0;

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Open Weekend Emails</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={openCreate} disabled={noTemplates || groups.length === 0}>
            <Plus size={14} style={{ marginRight: 4 }} /> New Schedule
          </button>
        )}
      </div>
      <p style={{ color: '#666', marginTop: 0, marginBottom: 20 }}>
        Each schedule checks for open weekends once a week and emails the group at a discount.
        If nothing is open that week, nothing is sent.
      </p>

      {error && <div className="approval-modal-error" style={{ marginBottom: 16 }}>{error}</div>}
      {notice && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#eef7ee', border: '1px solid #b7d9b7', borderRadius: 4, color: '#2b5d2b' }}>
          {notice}
        </div>
      )}
      {groups.length === 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fdf6e3', border: '1px solid #f0c36d', borderRadius: 4, color: '#7a5b1b' }}>
          No group is marked as a mailing list yet, so there is nothing to send to.
          Open <strong>My Groups</strong> and use <strong>Mark as list</strong> on the group you want to email.
        </div>
      )}
      {noTemplates && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fdf6e3', border: '1px solid #f0c36d', borderRadius: 4, color: '#7a5b1b' }}>
          There are no active email templates, so a schedule cannot be created yet.
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSave} style={{ background: '#f9f9f9', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>{editingId ? 'Edit Schedule' : 'New Schedule'}</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Group *</label>
              <select required value={form.groupId} onChange={(e) => setForm({ ...form, groupId: e.target.value })}>
                <option value="">Select a group...</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Template *</label>
              <select required value={form.templateId} onChange={(e) => setForm({ ...form, templateId: e.target.value })}>
                <option value="">Select a template...</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Send on *</label>
              <select value={form.dayOfWeek} onChange={(e) => setForm({ ...form, dayOfWeek: e.target.value })}>
                {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Look ahead (days) *</label>
              <input
                type="number" min="1" max="90" required
                value={form.lookaheadDays}
                onChange={(e) => setForm({ ...form, lookaheadDays: e.target.value })}
              />
              <small style={{ color: '#666' }}>How far forward to search from tomorrow.</small>
            </div>
            <div className="form-group">
              <label>Discount (% off renter rate) *</label>
              <input
                type="number" min="0" max="99" step="1" required
                value={form.discountPercent}
                onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Timezone</label>
              <input
                type="text"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                placeholder="America/New_York"
              />
              <small style={{ color: '#666' }}>Which day counts as the send day.</small>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: 14, margin: '4px 0 18px' }}>
            <strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Eye size={15} /> What would go out if this ran today
            </strong>
            {!preview && <span style={{ color: '#666' }}>Checking...</span>}
            {preview && preview.weekendCount === 0 && (
              <span style={{ color: '#666' }}>
                No open weekends in the next {form.lookaheadDays} days
                {preview.freeNightCount > 0 && ` (${preview.freeNightCount} free nights, but no Friday + Saturday pair with a rate set)`}.
                {' '}Nothing would be sent.
              </span>
            )}
            {preview && preview.weekendCount > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {preview.weekends.map((w) => (
                  <li key={w.checkIn} style={{ marginBottom: 2 }}>
                    {formatDay(w.checkIn)} &ndash; {formatDay(w.checkOut)}
                    {' — '}<strong>{money(w.discountedTotal)}</strong>
                    <span style={{ color: '#888', textDecoration: 'line-through', marginLeft: 6 }}>{money(w.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Schedule'}
            </button>
            <button type="button" className="btn btn-neutral" onClick={cancelForm}>Cancel</button>
          </div>
        </form>
      )}

      {schedules.length === 0 && !showForm && (
        <p style={{ color: '#666' }}>No schedules yet.</p>
      )}

      {schedules.map((s) => (
        <div key={s.id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 12, background: s.is_active ? '#fff' : '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {s.group_name}
                {!s.is_active && <span style={{ marginLeft: 8, fontSize: '0.8rem', color: '#888' }}>(paused)</span>}
              </div>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>
                Every {DAY_NAMES[s.day_of_week]} · {Number(s.discount_percent)}% off ·
                {' '}looking {s.lookahead_days} days ahead · {s.template_name || 'no template'}
              </div>
              <div style={{ color: '#666', fontSize: '0.9rem', marginTop: 4 }}>
                {s.opted_in_count} of {s.member_count} opted in
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-neutral" onClick={() => handleRunNow(s)} disabled={running === s.id} title="Queue this now">
                <Send size={14} style={{ marginRight: 4 }} />
                {running === s.id ? 'Running...' : 'Run now'}
              </button>
              <button className="btn btn-neutral" onClick={() => openEdit(s)} title="Edit"><Edit2 size={14} /></button>
              <button className="btn btn-neutral" onClick={() => handleDelete(s.id)} title="Delete"><Trash2 size={14} /></button>
            </div>
          </div>

          {s.opted_in_count === 0 && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fdf6e3', border: '1px solid #f0c36d', borderRadius: 4, color: '#7a5b1b', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0 }} />
              Nobody in this group has opted in to marketing email, so this schedule sends nothing.
              They each need to opt in from their preferences page.
            </div>
          )}

          {s.group_is_mailing_list === false && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fdf6e3', border: '1px solid #f0c36d', borderRadius: 4, color: '#7a5b1b', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0 }} />
              <span><strong>{s.group_name}</strong> is not marked as a mailing list, so this schedule will not send.
              Open My Groups and use <strong>Mark as list</strong> on it.</span>
            </div>
          )}

          {s.template_is_active === false && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fdf6e3', border: '1px solid #f0c36d', borderRadius: 4, color: '#7a5b1b', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0 }} />
              This template is inactive, so sends will be blocked.
            </div>
          )}
        </div>
      ))}

      {log.length > 0 && (
        <>
          <h3 style={{ marginTop: 32, marginBottom: 4 }}>Recent runs</h3>
          {logMeta && (
            <p style={{ color: '#666', fontSize: '0.88rem', marginTop: 0 }}>
              Sent from{' '}
              <a href={`mailto:${logMeta.sendingAddress}`}>{logMeta.sendingAddress}</a>
              {logMeta.replyTo && (
                <> · replies go to <a href={`mailto:${logMeta.replyTo}`}>{logMeta.replyTo}</a></>
              )}
              {logMeta.queueLastRunUtc && (
                <> · queue last ran {new Date(logMeta.queueLastRunUtc).toLocaleString()}</>
              )}
            </p>
          )}

          {log.map((r) => {
            const waiting = r.recipients.filter((x) => x.status === 'queued').length;
            const isOpen = openRun === r.id;
            return (
              <div key={r.id} style={{ border: '1px solid #ddd', borderRadius: 6, marginBottom: 8, background: '#fff' }}>
                <button
                  type="button"
                  onClick={() => setOpenRun(isOpen ? null : r.id)}
                  style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}
                >
                  <span>
                    <strong>{formatDay(r.run_date)}</strong>
                    <span style={{ color: '#666' }}> · {r.trigger_type} · {r.weekends_found} weekend{r.weekends_found === 1 ? '' : 's'}</span>
                  </span>
                  <span style={{ color: r.skipped_reason ? '#7a5b1b' : '#2b5d2b', fontSize: '0.9rem' }}>
                    {r.skipped_reason
                      ? (SKIP_REASONS[r.skipped_reason] || r.skipped_reason)
                      : `${r.recipients_queued} queued`}
                    <span style={{ color: '#999', marginLeft: 8 }}>{isOpen ? '▾' : '▸'}</span>
                  </span>
                </button>

                {isOpen && (
                  <div style={{ borderTop: '1px solid #eee', padding: '10px 14px' }}>
                    {r.recipients.length === 0 && (
                      <p style={{ color: '#666', margin: 0, fontSize: '0.9rem' }}>
                        No emails were created for this run.
                      </p>
                    )}

                    {waiting > 0 && (
                      <p style={{ margin: '0 0 8px', fontSize: '0.86rem', color: '#5a5a5a' }}>
                        {waiting} still waiting. The queue runner sends every couple of hours —
                        nothing is wrong, they have not been picked up yet.
                      </p>
                    )}

                    {r.recipients.map((x) => {
                      const s = describeRecipient(x);
                      return (
                        <div key={x.email} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', fontSize: '0.9rem', borderBottom: '1px solid #f4f4f4' }}>
                          <span>
                            <a href={`mailto:${x.email}`}>{x.email}</a>
                            {x.name && <span style={{ color: '#888' }}> · {x.name}</span>}
                          </span>
                          <span style={{ color: TONE_COLOR[s.tone], whiteSpace: 'nowrap' }}>
                            {s.label}
                            {s.detail && (
                              <span style={{ color: '#8a2f2f' }} title={s.detail}> — {s.detail.slice(0, 60)}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default VacancyEmailSchedule;
